import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar, User, Phone, Loader2, PartyPopper, Frown, CalendarDays, ArrowRight, CheckCircle } from "lucide-react";
import AvailabilityCalendar from "./AvailabilityCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem("availability_session_id");
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("availability_session_id", sessionId);
  }
  return sessionId;
};

const AvailabilityChecker = ({ onAvailabilityChecked }) => {
  const [sessionId] = useState(getSessionId);
  const [leadId, setLeadId] = useState(null);
  
  // Form state
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  
  // Calendar selection state
  const [showCalendar, setShowCalendar] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [hasChecked, setHasChecked] = useState(false);
  
  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Track widget view
  useEffect(() => {
    trackEvent("availability_widget_viewed");
  }, []);

  const trackEvent = useCallback(async (eventName, payload = {}) => {
    try {
      await axios.post(`${API}/track-activity`, {
        session_id: sessionId,
        lead_id: leadId,
        event_name: eventName,
        event_payload: payload,
        page_url: window.location.href
      });
    } catch (error) {
      console.error("Failed to track event:", error);
    }
  }, [sessionId, leadId]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!customerName.trim()) {
      newErrors.name = "Name is required";
    } else if (customerName.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    
    // Phone validation
    const cleanedPhone = phoneNumber.replace(/\D/g, "");
    if (!phoneNumber.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (cleanedPhone.length !== 10 || !/^[6-9]/.test(cleanedPhone)) {
      newErrors.phone = "Please enter a valid 10-digit Indian mobile number";
    }
    
    // Date validation
    if (!checkIn) {
      newErrors.checkIn = "Check-in date is required";
    }
    if (!checkOut) {
      newErrors.checkOut = "Check-out date is required";
    }
    if (checkIn && checkOut && checkOut <= checkIn) {
      newErrors.checkOut = "Check-out must be after check-in";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    const cleanedPhone = phoneNumber.replace(/\D/g, "");
    return (
      customerName.trim().length >= 2 &&
      cleanedPhone.length === 10 &&
      /^[6-9]/.test(cleanedPhone) &&
      checkIn &&
      checkOut &&
      checkOut > checkIn
    );
  };

  // Handle field blur for tracking
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Track events
    if (field === "name" && customerName.trim()) {
      trackEvent("name_entered", { name_length: customerName.trim().length });
    }
    if (field === "phone" && phoneNumber.trim()) {
      trackEvent("phone_entered", { phone_length: phoneNumber.replace(/\D/g, "").length });
    }
    if (field === "checkIn" && checkIn) {
      trackEvent("check_in_selected", { date: checkIn });
    }
    if (field === "checkOut" && checkOut) {
      trackEvent("check_out_selected", { date: checkOut });
    }
  };

  // Check availability
  const handleCheckAvailability = async () => {
    if (!validateForm()) {
      trackEvent("validation_error_shown", { errors: Object.keys(errors) });
      return;
    }

    setLoading(true);
    setResult(null);
    setSuggestions([]);
    trackEvent("check_availability_clicked");

    try {
      const response = await axios.post(`${API}/check-availability`, {
        session_id: sessionId,
        customer_name: customerName.trim(),
        phone_number: phoneNumber,
        check_in: checkIn,
        check_out: checkOut
      });

      setResult(response.data);
      setLeadId(response.data.lead_id);
      setSuggestions(response.data.suggestions || []);
      setHasChecked(true);
      
      // Notify parent component with customer data
      if (onAvailabilityChecked) {
        onAvailabilityChecked({
          customerName: customerName.trim(),
          phoneNumber: phoneNumber,
          checkIn: checkIn,
          checkOut: checkOut,
          isAvailable: response.data.available,
          leadId: response.data.lead_id
        });
      }
      
      // Show calendar after check
      setShowCalendar(true);
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to check availability";
      setErrors({ submit: errorMessage });
      trackEvent("validation_error_shown", { error: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // Handle calendar date selection
  const handleCalendarDateSelect = (newCheckIn, newCheckOut) => {
    setCheckIn(newCheckIn);
    setCheckOut(newCheckOut);
    setResult(null);
    setHasChecked(false);
    trackEvent("calendar_date_selected", { checkIn: newCheckIn, checkOut: newCheckOut });
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setCheckIn(suggestion.check_in);
    setCheckOut(suggestion.check_out);
    setResult(null);
    trackEvent("suggestion_clicked", suggestion);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-IN", { 
      day: "numeric", 
      month: "short", 
      year: "numeric" 
    });
  };

  // Get min date (today)
  const getMinDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  return (
    <div className="breakdown-card border-2 border-[#B08968]/30 bg-gradient-to-br from-[#FFFCF8] to-[#F5EDE4]" data-testid="availability-checker">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-[#B08968]/20 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-[#B08968]" />
        </div>
        <div>
          <h2 className="text-xl font-medium font-serif text-[#2D2D2D]">Check Date Availability</h2>
          <p className="text-sm text-muted-foreground">Let's find the perfect dates for your celebration</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Customer Name */}
        <div>
          <Label htmlFor="customer-name" className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-[#B08968]" />
            Your Name
          </Label>
          <Input
            id="customer-name"
            data-testid="availability-name-input"
            placeholder="Enter your full name"
            value={customerName}
            onChange={(e) => {
              setCustomerName(e.target.value);
              if (result) setResult(null);
            }}
            onBlur={() => handleBlur("name")}
            className={touched.name && errors.name ? "border-red-500" : ""}
          />
          {touched.name && errors.name && (
            <p className="text-red-500 text-xs mt-1">{errors.name}</p>
          )}
        </div>

        {/* Phone Number */}
        <div>
          <Label htmlFor="phone-number" className="flex items-center gap-2 mb-2">
            <Phone className="w-4 h-4 text-[#B08968]" />
            Phone Number
          </Label>
          <Input
            id="phone-number"
            data-testid="availability-phone-input"
            type="tel"
            placeholder="10-digit mobile number"
            value={phoneNumber}
            onChange={(e) => {
              // Allow only numbers
              const value = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
              setPhoneNumber(value);
              if (result) setResult(null);
            }}
            onBlur={() => handleBlur("phone")}
            className={touched.phone && errors.phone ? "border-red-500" : ""}
          />
          {touched.phone && errors.phone && (
            <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
          )}
        </div>

        {/* Date Pickers */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="check-in" className="flex items-center gap-2 mb-2">
              <CalendarDays className="w-4 h-4 text-[#B08968]" />
              Check-in
            </Label>
            <Input
              id="check-in"
              data-testid="availability-checkin-input"
              type="date"
              value={checkIn}
              min={getMinDate()}
              onChange={(e) => {
                setCheckIn(e.target.value);
                if (result) setResult(null);
                // Auto-set checkout to next day if not set
                if (!checkOut || e.target.value >= checkOut) {
                  const nextDay = new Date(e.target.value);
                  nextDay.setDate(nextDay.getDate() + 1);
                  setCheckOut(nextDay.toISOString().split("T")[0]);
                }
              }}
              onBlur={() => handleBlur("checkIn")}
              className={touched.checkIn && errors.checkIn ? "border-red-500" : ""}
            />
            {touched.checkIn && errors.checkIn && (
              <p className="text-red-500 text-xs mt-1">{errors.checkIn}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="check-out" className="flex items-center gap-2 mb-2">
              <CalendarDays className="w-4 h-4 text-[#B08968]" />
              Check-out
            </Label>
            <Input
              id="check-out"
              data-testid="availability-checkout-input"
              type="date"
              value={checkOut}
              min={checkIn || getMinDate()}
              onChange={(e) => {
                setCheckOut(e.target.value);
                if (result) setResult(null);
              }}
              onBlur={() => handleBlur("checkOut")}
              className={touched.checkOut && errors.checkOut ? "border-red-500" : ""}
            />
            {touched.checkOut && errors.checkOut && (
              <p className="text-red-500 text-xs mt-1">{errors.checkOut}</p>
            )}
          </div>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <p className="text-red-500 text-sm text-center">{errors.submit}</p>
        )}

        {/* CTA Button */}
        <Button
          onClick={handleCheckAvailability}
          disabled={loading || !isFormValid()}
          data-testid="check-availability-btn"
          className="w-full bg-[#B08968] hover:bg-[#9A7558] text-white rounded-full py-6 text-lg font-medium transition-all duration-300 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Checking...
            </>
          ) : (
            <>
              <Calendar className="w-5 h-5 mr-2" />
              Check Availability
            </>
          )}
        </Button>

        {/* Result Message */}
        {result && (
          <div 
            className={`mt-6 p-6 rounded-xl text-center transition-all duration-500 ${
              result.available 
                ? "bg-green-50 border-2 border-green-200" 
                : "bg-red-50 border-2 border-red-200"
            }`}
            data-testid="availability-result"
          >
            {result.available ? (
              <>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <PartyPopper className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-medium text-green-800 mb-2">
                  YES! 🎉 Your dates are available!
                </h3>
                <p className="text-green-700">
                  This is going to be a beautiful celebration. Let's take the next step.
                </p>
                <p className="text-green-600 text-sm mt-3 flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  You can now proceed to check the detailed costing below.
                </p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <Frown className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-medium text-red-800 mb-2">
                  Ah no 😔 These dates are already booked
                </h3>
                <p className="text-red-700 mb-2">
                  Please try alternate dates and we'll help you find the perfect option.
                </p>
                <p className="text-amber-700 text-sm bg-amber-50 px-3 py-2 rounded-lg inline-block">
                  You can still proceed to check the costing for your preferred dates below.
                </p>
              </>
            )}

            {/* Visual Calendar - Shows after any result */}
            {showCalendar && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">
                  {result.available ? "Or select different dates:" : "Select available dates from the calendar:"}
                </p>
                <AvailabilityCalendar
                  onDateSelect={handleCalendarDateSelect}
                  selectedCheckIn={checkIn}
                  selectedCheckOut={checkOut}
                />
                {checkIn && checkOut && !hasChecked && (
                  <Button
                    onClick={handleCheckAvailability}
                    disabled={loading}
                    className="mt-4 bg-[#B08968] hover:bg-[#9A7558] text-white rounded-full px-8"
                  >
                    {loading ? "Checking..." : "Check Selected Dates"}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityChecker;
